import React from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight, faHeart, faEye, faCheck, faStar } from "@fortawesome/free-solid-svg-icons";
import Navbar from "../Components/Navbar";
import Footer from "../Components/Footer";
import useScrollReveal from "../hooks/useScrollReveal";
import "../Styles/About.css";

function About() {
  useScrollReveal();

  return (
    <div className="about-page">
      <Navbar />

      {/* Hero */}
      <section className="page-hero">
        <div className="page-hero-particles">
          <span className="ph-particle ph-particle-1"></span>
          <span className="ph-particle ph-particle-2"></span>
          <span className="ph-particle ph-particle-3"></span>
          <span className="ph-particle ph-particle-4"></span>
          <span className="ph-particle ph-particle-5"></span>
        </div>
        <div className="container">
          <span className="section-tag">About Us</span>
          <h1 className="page-hero-title">Built On Trust.<br/>Driven By Purpose.</h1>
          <p className="page-hero-subtitle">
            For over two decades, we've helped families across America achieve financial peace of mind through education, strategy, and genuine partnership.
          </p>
        </div>
      </section>

      {/* Story */}
      <section className="about-story section-padding reveal">
        <div className="container">
          <div className="about-story-grid">
            <div className="about-story-left">
              <span className="section-tag">Our Story</span>
              <h2 className="section-title">A Mission That Started With One Family.</h2>
              <p className="about-story-text">
                Ebenezer Family Group began with a simple observation: too many families 
                were making critical financial decisions without proper guidance. The 
                consequences — inadequate protection, missed opportunities, and unnecessary 
                risk — were devastating.
              </p>
              <p className="about-story-text">
                We set out to change that. Today, we serve over 1,000 families across 45+ 
                states, providing the education, tools, and personalized strategies they need 
                to build lasting financial security.
              </p>
              <p className="about-story-text">
                Our approach is different. We don't just sell products — we educate, we listen, 
                and we build relationships that last generations.
              </p>
            </div>
            <div className="about-story-right">
              <div className="about-stats-visual">
                <div className="asv-card asv-card-1">
                  <span className="asv-num">20+</span>
                  <span className="asv-label">Years Experience</span>
                </div>
                <div className="asv-card asv-card-2">
                  <span className="asv-num">1000+</span>
                  <span className="asv-label">Families Served</span>
                </div>
                <div className="asv-card asv-card-3">
                  <span className="asv-num">45+</span>
                  <span className="asv-label">States Licensed</span>
                </div>
                <div className="asv-card asv-card-4">
                  <span className="asv-num">98%</span>
                  <span className="asv-label">Satisfaction</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="about-mission section-padding reveal">
        <div className="container">
          <div className="mission-grid">
            <div className="mission-card">
              <div className="mission-icon">
                <FontAwesomeIcon icon={faEye} />
              </div>
              <h3>Our Vision</h3>
              <p>
                A world where every family — regardless of background or income — 
                has access to world-class financial guidance and the knowledge to make 
                confident decisions about their future.
              </p>
            </div>
            <div className="mission-card">
              <div className="mission-icon">
                <FontAwesomeIcon icon={faHeart} />
              </div>
              <h3>Our Mission</h3>
              <p>
                To empower families through financial education and personalized planning, 
                helping them build protection, accumulate wealth, and create legacies that 
                last for generations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="about-values section-padding reveal">
        <div className="container">
          <div className="values-header">
            <span className="section-tag">Our Core Values</span>
            <h2 className="section-title">What Guides Everything We Do</h2>
          </div>
          <div className="values-grid">
            <div className="value-card">
              <span className="value-num">01</span>
              <h4>Education First</h4>
              <p>We believe informed decisions are the best decisions. Every interaction is an opportunity to teach and empower.</p>
            </div>
            <div className="value-card">
              <span className="value-num">02</span>
              <h4>Client-Centered</h4>
              <p>Your goals drive our recommendations. We never push products — we solve problems and build solutions.</p>
            </div>
            <div className="value-card">
              <span className="value-num">03</span>
              <h4>Full Transparency</h4>
              <p>No hidden fees, no confusing jargon, no surprises. Just clear, honest guidance every step of the way.</p>
            </div>
            <div className="value-card">
              <span className="value-num">04</span>
              <h4>Long-Term Thinking</h4>
              <p>We build strategies for decades, not quarters. Your family's lasting legacy is our true measure of success.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="about-why section-padding reveal">
        <div className="container">
          <div className="about-why-grid">
            <div className="about-why-left">
              <span className="section-tag">Why Choose Us</span>
              <h2 className="section-title">The Ebenezer Advantage</h2>
              <div className="why-features">
                <div className="why-feature-item">
                  <div className="why-f-icon"><FontAwesomeIcon icon={faCheck} /></div>
                  <div>
                    <h5>Holistic Planning</h5>
                    <p>We look at your complete financial picture, not just one piece of the puzzle.</p>
                  </div>
                </div>
                <div className="why-feature-item">
                  <div className="why-f-icon"><FontAwesomeIcon icon={faCheck} /></div>
                  <div>
                    <h5>Family-First Focus</h5>
                    <p>Every recommendation considers your family's unique needs and goals.</p>
                  </div>
                </div>
                <div className="why-feature-item">
                  <div className="why-f-icon"><FontAwesomeIcon icon={faCheck} /></div>
                  <div>
                    <h5>Ongoing Support</h5>
                    <p>We don't disappear after implementation. We're your lifelong financial partners.</p>
                  </div>
                </div>
                <div className="why-feature-item">
                  <div className="why-f-icon"><FontAwesomeIcon icon={faCheck} /></div>
                  <div>
                    <h5>Proven Results</h5>
                    <p>20+ years of helping families achieve financial peace of mind.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="about-why-right">
              <div className="about-testimonial-card">
                <div className="atc-stars">
                  {[...Array(5)].map((_, i) => <FontAwesomeIcon key={i} icon={faStar} />)}
                </div>
                <p className="atc-text">
                  "Working with Ebenezer transformed our entire financial outlook. 
                  They didn't just sell us a policy — they educated us and gave us 
                  a complete strategy for our family's future."
                </p>
                <span className="atc-author">— The Williams Family, Minneapolis</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="about-cta section-padding reveal">
        <div className="container">
          <div className="about-cta-box">
            <h2>Ready to work with a team that puts your family first?</h2>
            <p>Schedule a free consultation and discover how we can help secure your family's financial future.</p>
            <Link to="/schedule-appointment" className="btn-primary">
              Book Your Consultation <FontAwesomeIcon icon={faArrowRight} />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default About;
